// utils/normalizeImage.ts
export async function normalizeImage(
  src: string | File | Blob,
  opts: { maxDim?: number; mime?: string; quality?: number } = {},
): Promise<string> {
  const { maxDim = 4096, mime = 'image/jpeg', quality = 0.92 } = opts

  // 1) Resolve input into a Blob fast
  const blob =
    typeof src === 'string'
      ? await (await fetch(src)).blob()
      : src instanceof File || src instanceof Blob
      ? src
      : (() => {
          throw new Error('Unsupported src')
        })()

  const originalType = blob.type || 'image/jpeg'

  // Helper: async encode to blob, then return a blob: URL
  const encodeToObjectURL = async (image: ImageBitmap | HTMLImageElement, w: number, h: number) => {
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx)
      throw new Error('No 2D context')
      // Better quality on downsizing
    ;(ctx as any).imageSmoothingEnabled = true
    ;(ctx as any).imageSmoothingQuality = 'high'
    ctx.drawImage(image as any, 0, 0, w, h)

    const outBlob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), mime, quality),
    )
    return URL.createObjectURL(outBlob)
  }

  // 2) Try the fast path: decode once to read size, then re-decode already resized
  try {
    // First small decode to get the natural size with proper EXIF orientation
    let probe = await createImageBitmap(blob, { imageOrientation: 'from-image' as any })
    const width = probe.width
    const height = probe.height
    probe.close?.()

    const scale = Math.min(1, maxDim / Math.max(width, height))
    const outW = Math.max(1, Math.round(width * scale))
    const outH = Math.max(1, Math.round(height * scale))

    // If no resize AND we’re not converting mime -> return original blob quickly
    if (scale === 1 && (mime === originalType || !mime)) {
      return URL.createObjectURL(blob) // ⚡ fastest path
    }

    // Decode again, already resized by the browser (very fast)
    const bmp = await createImageBitmap(blob, {
      imageOrientation: 'from-image' as any,
      resizeWidth: outW,
      resizeHeight: outH,
      resizeQuality: 'high' as any,
    })

    const url = await encodeToObjectURL(bmp, outW, outH)
    bmp.close?.()
    return url
  } catch {
    // Fallback path (older browsers / Safari without resize options)
  }

  // 3) Fallback: HTMLImageElement + canvas
  const loadHTMLImage = () =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.decoding = 'async'
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = URL.createObjectURL(blob)
    })

  const img = await loadHTMLImage()
  const width = img.naturalWidth || img.width
  const height = img.naturalHeight || img.height
  const scale = Math.min(1, maxDim / Math.max(width, height))
  const outW = Math.max(1, Math.round(width * scale))
  const outH = Math.max(1, Math.round(height * scale))

  const url = await (async () => {
    // If no resize AND no conversion needed, just reuse original blob
    if (scale === 1 && (mime === originalType || !mime)) {
      const u = URL.createObjectURL(blob)
      URL.revokeObjectURL(img.src)
      return u
    }
    const u = await encodeToObjectURL(img, outW, outH)
    URL.revokeObjectURL(img.src)
    return u
  })()

  return url
}
